// Big files in the browser: hashing a piece at a time (crypto.subtle has no streaming) and reading a video's length,
// size and frame rate before it is uploaded. asset_version is immutable and the worker's probe does not record them,
// so what the file says is read here and sent along once, with the upload.
import type { VideoMeta } from "@filmsquare/edit/mp4";

/** How much of a file is in memory while it is hashed. */
export const HASH_CHUNK_BYTES = 8 * 1024 * 1024;

export interface UploadProgress {
  /** "hash": reading the file; "put": sending it; "verify": the server checks what it got */
  phase: "hash" | "put" | "verify";
  /** 0 to 1 */
  fraction: number;
}

/**
 * The SHA-256 of a Blob or File: what fits one chunk natively, the rest in chunks with a library that is only loaded for it. Never
 * more than one chunk in memory, and the page stays responsive. crypto.subtle is missing on plain http, as on a self-host in a LAN.
 */
export async function sha256OfBlob(
  blob: Blob,
  onProgress?: (fraction: number) => void,
  chunkBytes = HASH_CHUNK_BYTES,
  signal?: AbortSignal,
): Promise<string> {
  if (blob.size <= chunkBytes && globalThis.crypto?.subtle) {
    signal?.throwIfAborted();
    const digest = await crypto.subtle.digest("SHA-256", await blob.slice(0, chunkBytes).arrayBuffer());
    if (blob.size > 0) onProgress?.(1);
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
  }
  const [{ sha256 }, { bytesToHex }] = await Promise.all([
    import("@noble/hashes/sha2.js"),
    import("@noble/hashes/utils.js"),
  ]);
  const h = sha256.create();
  for (let at = 0; at < blob.size; at += chunkBytes) {
    signal?.throwIfAborted();
    h.update(new Uint8Array(await blob.slice(at, at + chunkBytes).arrayBuffer()));
    onProgress?.(Math.min(1, (at + chunkBytes) / blob.size));
  }
  return bytesToHex(h.digest());
}

// ── what a video file says ────────────────────────────────────────────────────────────────────────────────

export type { VideoMeta };

/** What the browser itself reads from the file: length and display size (rotation applied). Null when it cannot. */
function browserMeta(file: Blob, timeoutMs = 15_000): Promise<VideoMeta | null> {
  return new Promise((resolve) => {
    if (typeof document === "undefined" || typeof URL.createObjectURL !== "function") {
      resolve(null);
      return;
    }
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    const done = (m: VideoMeta | null) => {
      clearTimeout(timer);
      v.removeAttribute("src");
      v.load();
      URL.revokeObjectURL(url);
      resolve(m);
    };
    const timer = setTimeout(() => done(null), timeoutMs);
    v.onerror = () => done(null);
    v.onloadedmetadata = () => {
      const m: VideoMeta = {};
      if (Number.isFinite(v.duration) && v.duration > 0) m.durationS = v.duration;
      if (v.videoWidth > 0 && v.videoHeight > 0) {
        m.width = v.videoWidth;
        m.height = v.videoHeight;
      }
      done(m);
    };
    v.src = url;
  });
}

/**
 * A video file's length, size and frame rate: the browser's own reading first (it applies rotation), the file's boxes
 * for the rest. The frame rate comes only from the boxes (a browser does not say): a WebM has none, and its notes
 * then count frames at the app's default, which still lands them on the right moment.
 */
export async function readVideoMeta(file: Blob): Promise<VideoMeta> {
  const [boxes, shown] = await Promise.all([
    import("@filmsquare/edit/mp4")
      .then(({ mp4Meta }) =>
        mp4Meta(file.size, async (start, end) => new Uint8Array(await file.slice(start, end).arrayBuffer())),
      )
      .catch(() => null),
    browserMeta(file),
  ]);
  const merged: VideoMeta = { ...boxes, ...shown };
  if (merged.fps !== undefined && (merged.fps <= 0 || merged.fps > 999)) delete merged.fps;
  return merged;
}
